import { useCallback, useEffect, useRef, useState } from 'react'

interface VideoState {
  currentTime: number
  duration: number
  /** End time (in seconds) of the furthest buffered range. */
  buffered: number
  playing: boolean
  volume: number
  muted: boolean
}

const INITIAL_VIDEO_STATE: VideoState = {
  currentTime: 0,
  duration: 0,
  buffered: 0,
  playing: false,
  volume: 100,
  muted: false,
}

/**
 * Subscribes to a video element's native events and returns reactive state
 * for currentTime, duration, buffered range, playing, volume, and muted.
 *
 * This hook reads directly from the HTMLVideoElement so the HLS.js UI controls
 * have frame-accurate data without depending on AngelPlayerContext timing.
 */
export function useVideoState(videoRef: React.RefObject<HTMLVideoElement | null>): VideoState {
  const [state, setState] = useState<VideoState>(INITIAL_VIDEO_STATE)
  const rafRef = useRef<number | null>(null)

  const getBufferedEnd = useCallback((video: HTMLVideoElement): number => {
    if (video.buffered.length === 0) return 0
    return video.buffered.end(video.buffered.length - 1)
  }, [])

  // Continuously update currentTime + buffered while playing via requestAnimationFrame.
  // This gives smoother seek bar updates than relying on the `timeupdate` event (~250ms).
  const tick = useCallback(() => {
    const video = videoRef.current
    if (!video) return

    setState((prev) => {
      const currentTime = video.currentTime
      const buffered = getBufferedEnd(video)
      if (prev.currentTime === currentTime && prev.buffered === buffered) return prev
      return { ...prev, currentTime, buffered }
    })

    rafRef.current = requestAnimationFrame(tick)
  }, [videoRef, getBufferedEnd])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const syncAll = () => {
      setState({
        currentTime: video.currentTime,
        duration: video.duration || 0,
        buffered: getBufferedEnd(video),
        playing: !video.paused && !video.ended,
        volume: video.volume * 100,
        muted: video.muted,
      })
    }

    const onPlay = () => {
      setState((prev) => ({ ...prev, playing: true }))
      rafRef.current = requestAnimationFrame(tick)
    }

    const onPause = () => {
      setState((prev) => ({ ...prev, playing: false }))
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    const onDurationChange = () => {
      setState((prev) => ({ ...prev, duration: video.duration || 0 }))
    }

    const onVolumeChange = () => {
      setState((prev) => ({ ...prev, volume: video.volume * 100, muted: video.muted }))
    }

    const onSeeked = () => {
      setState((prev) => ({ ...prev, currentTime: video.currentTime }))
    }

    video.addEventListener('loadedmetadata', syncAll)
    video.addEventListener('playing', onPlay)
    video.addEventListener('pause', onPause)
    video.addEventListener('ended', onPause)
    video.addEventListener('durationchange', onDurationChange)
    video.addEventListener('volumechange', onVolumeChange)
    video.addEventListener('seeked', onSeeked)

    // Sync initial state in case the video is already loaded.
    syncAll()

    return () => {
      video.removeEventListener('loadedmetadata', syncAll)
      video.removeEventListener('playing', onPlay)
      video.removeEventListener('pause', onPause)
      video.removeEventListener('ended', onPause)
      video.removeEventListener('durationchange', onDurationChange)
      video.removeEventListener('volumechange', onVolumeChange)
      video.removeEventListener('seeked', onSeeked)

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [videoRef, tick, getBufferedEnd])

  return state
}
